import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Typography
} from '@mui/material';
import {
  Close,
  Tune,
  Home,
  BarChart,
  ShowChart,
  CalendarMonth,
  AttachMoney,
  EmojiEvents,
  Forum
} from '@mui/icons-material';

interface Props {
  open: boolean;
  onClose: () => void;
}

const steps = [
  {
    title: 'まずは初期設定',
    body: '最初に、設定ページで月の目標金額、休みにする日、使う配達サービスを登録します。ここが整うと、ダッシュボードやカレンダーの集計が使いやすくなります。',
    icon: <Tune />
  },
  {
    title: 'ホーム',
    body: '今日の売上、目標への進み具合、平均時給、サービス別の内訳を確認するページです。日々の確認はここから始めます。',
    icon: <Home />
  },
  {
    title: '履歴',
    body: '過去の配達記録を確認するページです。カードの「詳細」を開くと、稼働時間、休憩、メモ、編集、削除ができます。',
    icon: <BarChart />
  },
  {
    title: '分析',
    body: '売上、利益、時給、件数、サービス別売上を切り替えて確認できます。期間を変えると、その期間だけの推移を見られます。',
    icon: <ShowChart />
  },
  {
    title: 'カレンダー',
    body: '月ごとの売上、支出、利益、配達件数を確認できます。日付を押すと、その日の収支が下のカードに表示されます。',
    icon: <CalendarMonth />
  },
  {
    title: '支出',
    body: 'ガソリン代、駐車場代、消耗品などの支出を記録します。売上と合わせて、利益を把握するために使います。',
    icon: <AttachMoney />
  },
  {
    title: 'ランキング',
    body: 'ランキング機能を有効にすると、売上や時給を他のユーザーと比べられます。公開範囲を確認してから使ってください。',
    icon: <EmojiEvents />
  },
  {
    title: '掲示板',
    body: '利用者同士で情報共有できるページです。投稿やコメントを見ることで、配達に役立つ情報を集められます。',
    icon: <Forum />
  }
];

function OnboardingTutorial({ open, onClose }: Props) {
  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  useEffect(() => {
    if (open) {
      setStepIndex(0);
    }
  }, [open]);

  const handleNext = () => {
    if (isLast) {
      onClose();
      return;
    }
    setStepIndex((value) => value + 1);
  };

  const handleBack = () => {
    setStepIndex((value) => Math.max(0, value - 1));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          bgcolor: '#101512',
          color: 'white',
          borderRadius: '16px',
          border: '1px solid rgba(16,185,129,0.28)',
          m: 2,
          overflow: 'hidden'
        }
      }}
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(0,0,0,0.66)',
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography fontSize={15} color="#8a948d" fontWeight={700}>
            使い方ガイド
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: '#8a948d' }} aria-label="チュートリアルを閉じる">
            <Close />
          </IconButton>
        </Box>
        <LinearProgress
          variant="determinate"
          value={((stepIndex + 1) / steps.length) * 100}
          sx={{
            mt: 1.25,
            height: 6,
            borderRadius: 3,
            bgcolor: '#1d2a23',
            '& .MuiLinearProgress-bar': { bgcolor: '#10B981', borderRadius: 3 }
          }}
        />
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: '14px',
              bgcolor: 'rgba(16,185,129,0.14)',
              color: '#34D399',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg': { fontSize: 30 }
            }}
          >
            {step.icon}
          </Box>
          <Box
            component="img"
            src="/assets/normal-character-transparent.png"
            alt=""
            sx={{
              ml: 'auto',
              width: 112,
              height: 136,
              objectFit: 'contain',
              objectPosition: 'bottom center',
              display: { xs: 'block', sm: 'block' },
              p: 0.5,
              borderRadius: '18px',
              background: 'linear-gradient(180deg, rgba(16,185,129,0.16), rgba(16,185,129,0.03))',
              border: '1px solid rgba(255,255,255,0.08)',
              filter: 'drop-shadow(0 12px 22px rgba(0,0,0,0.36))'
            }}
          />
        </Box>

        <Typography fontSize={24} fontWeight={800} sx={{ mb: 1 }}>
          {step.title}
        </Typography>
        <Typography fontSize={16} color="#c5cec9" sx={{ lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
          {step.body}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.75, mt: 2.5 }}>
          {steps.map((_, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: index <= stepIndex ? '#10B981' : 'rgba(255,255,255,0.12)'
              }}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: '#8a948d', minWidth: 72 }}
        >
          スキップ
        </Button>
        <Button
          variant="outlined"
          disabled={stepIndex === 0}
          onClick={handleBack}
          sx={{ borderColor: '#304039', color: '#c5cec9' }}
        >
          戻る
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          sx={{ bgcolor: '#10B981', color: '#06110c', '&:hover': { bgcolor: '#34D399' } }}
        >
          {isLast ? '完了' : '次へ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OnboardingTutorial;
